import React, { useState, useEffect, useMemo } from 'react';
import { 
  Activity, 
  Clock, 
  User, 
  Search, 
  Filter, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Download,
  Calendar,
  Eye,
  Edit2,
  Upload,
  Mail,
  UserPlus,
  MessageSquare,
  Settings,
  LogIn,
  LogOut,
  FileText,
  Briefcase,
  Users2,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Info,
  X,
  ChevronDown
} from 'lucide-react';

// Activity Log Main Component
const ActivityLogSystem = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  const [paginationInfo, setPaginationInfo] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
    hasNext: false,
    hasPrev: false
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('today');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const itemsPerPage = 20;

  // Activity type configurations with fallback
  const activityTypes = {
    import: {
      label: 'Import Activity',
      icon: Upload,
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      bgColor: 'bg-blue-50'
    },
    login: {
      label: 'Login',
      icon: LogIn,
      color: 'bg-green-100 text-green-800 border-green-200',
      bgColor: 'bg-green-50'
    },
    logout: {
      label: 'Logout',
      icon: LogOut,
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      bgColor: 'bg-gray-50'
    },
    reporting_info: {
      label: 'Reporting Info',
      icon: Users2,
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      bgColor: 'bg-purple-50'
    },
    project_details: {
      label: 'Project Details',
      icon: Briefcase,
      color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      bgColor: 'bg-indigo-50'
    },
    client_feedback: {
      label: 'Client Feedback',
      icon: MessageSquare,
      color: 'bg-orange-100 text-orange-800 border-orange-200',
      bgColor: 'bg-orange-50'
    },
    edit_candidate: {
      label: 'Edit Information',
      icon: Edit2,
      color: 'bg-teal-100 text-teal-800 border-teal-200',
      bgColor: 'bg-teal-50'
    },
    extension_management: {
      label: 'Extension Management',
      icon: TrendingUp,
      color: 'bg-pink-100 text-pink-800 border-pink-200',
      bgColor: 'bg-pink-50'
    },
    system_action: {
      label: 'System Action',
      icon: Settings,
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      bgColor: 'bg-gray-50'
    },
    // Fallback for unknown activity types
    default: {
      label: 'Unknown Activity',
      icon: Activity,
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      bgColor: 'bg-gray-50'
    }
  };

  // Safe function to get activity type config
  const getActivityConfig = (activityType) => {
    return activityTypes[activityType] || activityTypes.default;
  };

  // Fetch real activity data from API
  const fetchActivities = async (filters = {}) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...filters
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (activityTypeFilter !== 'all') params.append('activity_type', activityTypeFilter);
      if (userFilter !== 'all') params.append('user_email', userFilter);
      if (dateRangeFilter !== 'all') {
        if (dateRangeFilter === 'custom' && customStartDate && customEndDate) {
          params.append('date_from', customStartDate);
          params.append('date_to', customEndDate);
        } else {
          params.append('date_range', dateRangeFilter);
        }
      }
      
      const response = await fetch(`https://test.vdpl.co/Candidates/Candidates/get_activity_log.php?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        const transformedActivities = result.data.activities.map(activity => ({
          id: activity.id,
          type: activity.activity_type,
          action: activity.action,
          description: activity.description,
          user: activity.user_name,
          userEmail: activity.user_email,
          timestamp: activity.created_at,
          details: activity.details || {},
          ipAddress: activity.ip_address,
          status: activity.status,
          errorMessage: activity.error_message
        }));
        
        setActivities(transformedActivities);
        
        if (result.data.pagination) {
          setPaginationInfo({
            total: result.data.pagination.total_items,
            totalPages: result.data.pagination.total_pages,
            currentPage: result.data.pagination.current_page,
            hasNext: result.data.pagination.has_next,
            hasPrev: result.data.pagination.has_prev
          });
        }
      } else {
        throw new Error(result.message || 'Failed to fetch activities');
      }
      
    } catch (error) {
      console.error('Error fetching activities:', error);
      setActivities([{
        id: 1,
        type: 'system_action',
        action: 'System Status',
        description: 'Activity log system is running. No activities found or connection issue.',
        user: 'System',
        userEmail: 'system@company.com',
        timestamp: new Date().toISOString(),
        details: { error: error.message },
        ipAddress: 'localhost',
        status: 'warning'
      }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [currentPage]);

  // Separate useEffect for filter changes  
  useEffect(() => {
    if (currentPage === 1) {
      fetchActivities();
    } else {
      setCurrentPage(1);
    }
  }, [searchTerm, activityTypeFilter, userFilter, dateRangeFilter, customStartDate, customEndDate]);

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchActivities();
  };

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);
    
    switch(filterType) {
      case 'search': setSearchTerm(value); break;
      case 'activityType': setActivityTypeFilter(value); break;
      case 'user': setUserFilter(value); break;
      case 'dateRange': 
        setDateRangeFilter(value);
        if (value !== 'custom') {
          setCustomStartDate('');
          setCustomEndDate('');
        }
        break;
      case 'customStartDate': setCustomStartDate(value); break;
      case 'customEndDate': setCustomEndDate(value); break;
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Pagination
  const paginatedActivities = activities;
  const totalPages = paginationInfo.totalPages;

  // Get unique users for filter
  const uniqueUsers = [...new Set(activities.map(a => a.user))];

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Activity detail modal
  const ActivityDetailModal = ({ activity, onClose }) => {
    if (!activity) return null;

    const config = getActivityConfig(activity.type);
    const Icon = config.icon;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className={`px-6 py-4 ${config.bgColor} border-b border-gray-200`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{activity.action}</h2>
                  <p className="text-sm text-gray-600">{formatTimestamp(activity.timestamp)}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-xl transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Activity Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">User</label>
                  <p className="text-sm text-gray-900">{activity.user}</p>
                  <p className="text-xs text-gray-500">{activity.userEmail}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">IP Address</label>
                  <p className="text-sm text-gray-900">{activity.ipAddress}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Type</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${config.color}`}>
                    {config.label}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
                    activity.status === 'success' 
                      ? 'bg-green-100 text-green-800 border-green-200' 
                      : activity.status === 'warning'
                      ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                      : 'bg-red-100 text-red-800 border-red-200'
                  }`}>
                    {activity.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{activity.description}</p>
            </div>

            {/* Details */}
            {activity.details && Object.keys(activity.details).length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Details</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(activity.details, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Activity Log</h1>
              <p className="text-gray-600">Track all system activities and user actions</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              className="flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => {/* Implement export */}}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="space-y-4">
          {/* Basic Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
              />
            </div>

            <select
              value={activityTypeFilter}
              onChange={(e) => handleFilterChange('activityType', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Activity Types</option>
              {Object.entries(activityTypes).filter(([key]) => key !== 'default').map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>

            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Users</option>
              {uniqueUsers.map(user => (
                <option key={user} value={user}>{user}</option>
              ))}
            </select>

            <select
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Custom Date Range */}
          {dateRangeFilter === 'custom' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activity Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(activityTypes).filter(([key]) => key !== 'default').map(([key, config]) => {
          const Icon = config.icon;
          const count = activities.filter(a => a.type === key).length;
          return (
            <div key={key} className={`${config.bgColor} rounded-xl p-4 border border-gray-200`}>
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">{count}</div>
                  <div className="text-xs text-gray-600">{config.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Activity List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Activities ({paginationInfo.total})
            </h2>
            <div className="text-sm text-gray-500">
              Showing {activities.length} of {paginationInfo.total} activities
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Loading activities...</p>
            </div>
          ) : paginatedActivities.length === 0 ? (
            <div className="p-8 text-center">
              <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No activities found</h3>
              <p className="text-gray-500">Try adjusting your search criteria</p>
            </div>
          ) : (
            paginatedActivities.map((activity) => {
              const config = getActivityConfig(activity.type);
              const Icon = config.icon;

              return (
                <div key={activity.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start space-x-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-sm font-semibold text-gray-900">{activity.action}</h3>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${config.color}`}>
                            {config.label}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-500">{formatTimestamp(activity.timestamp)}</span>
                          <button
                            onClick={() => setSelectedActivity(activity)}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                      
                      <div className="flex items-center space-x-4 mt-2">
                        <div className="flex items-center space-x-1">
                          <User className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{activity.user}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">{activity.ipAddress}</span>
                        </div>
                        {activity.status && (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            activity.status === 'success' 
                              ? 'bg-green-100 text-green-800' 
                              : activity.status === 'warning'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {activity.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {paginationInfo.currentPage} of {totalPages} ({paginationInfo.total} total activities)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Activity Detail Modal */}
      {selectedActivity && (
        <ActivityDetailModal
          activity={selectedActivity}
          onClose={() => setSelectedActivity(null)}
        />
      )}
    </div>
  );
};

export default ActivityLogSystem;